import { useEffect, useMemo, useRef, useState } from "react";

import { QRCodeSVG } from "qrcode.react";

import { Button, Heading, Input, Notification, Select, Textarea } from "@stellar/design-system";

import type {
  EngineTypes,
  PairingTypes,
  ProposalTypes,
  SessionTypes,
  SignClientTypes,
} from "@via/walletconnect-types";

import {
  DEFAULT_CONNECT_PARAMS,
  STELLAR_MAINNET_CHAIN_ID,
  STELLAR_METHOD_GET_ADDRESS,
  STELLAR_METHOD_SEND_TRANSACTION,
  STELLAR_METHOD_SIGN_MESSAGE,
  STELLAR_METHOD_SIGN_TRANSACTION,
  STELLAR_METHOD_TRUST_ASSET,
  STELLAR_TESTNET_CHAIN_ID,
} from "@/services/walletConnect/constants";
import { WcSignClient } from "@/services/walletConnect/wcSignClient";


const DEFAULT_USER_ID = "sdp-dummy-user";
const LOG_LIMIT = 40;

type Status =
  | "idle"
  | "initializing"
  | "ready"
  | "connecting"
  | "connected"
  | "error";

type LogEntry = { at: string; label: string; payload: unknown };

const CHAIN_OPTIONS = [
  { value: STELLAR_TESTNET_CHAIN_ID, label: `Testnet (${STELLAR_TESTNET_CHAIN_ID})` },
  { value: STELLAR_MAINNET_CHAIN_ID, label: `Pubnet (${STELLAR_MAINNET_CHAIN_ID})` },
] as const;

/**
 * Presets that match the VIA Wallet's Stellar RPC handler
 * (`via-mobile-wallet/src/app/core/chains/rpcs/stellar.rpc.ts`).
 *
 * NOTE: `stellar_getAddress` and `stellar_getBalance` are implemented in
 * the wallet's `handleRequest` but their `parseRequest` (approval-sheet
 * data view) currently throws `Unsupported method`. `stellar_getAddress`
 * is kept in the list so the SDP dashboard can drive the request once
 * that wallet-side gap is closed; today it will fail on the approval
 * sheet. `params` is always a JSON array; `params[0]` is the payload.
 */
const METHOD_PRESETS: Record<string, { label: string; params: unknown[] }> = {
  [STELLAR_METHOD_GET_ADDRESS]: {
    label: "stellar_getAddress (wallet parseRequest gap — will error today)",
    params: [],
  },
  [STELLAR_METHOD_SIGN_MESSAGE]: {
    label: "stellar_signMessage",
    params: ["Hello from SDP dashboard debug"],
  },
  [STELLAR_METHOD_SIGN_TRANSACTION]: {
    label: "stellar_signTransaction",
    params: [
      {
        destination: "GAAVIAIJZBW3SXCHTYZTUW5J2DOGHPGDAOZ664N3NJONYFAFFGFZMKUE",
        amount: "1.0000000",
        asset: { code: "XLM", native: true },
        memo: "SDP-WC-test",
        memoType: "text",
      },
    ],
  },
  [STELLAR_METHOD_SEND_TRANSACTION]: {
    label: "stellar_sendTransaction",
    params: [
      {
        destination: "GAAVIAIJZBW3SXCHTYZTUW5J2DOGHPGDAOZ664N3NJONYFAFFGFZMKUE",
        amount: "1.0000000",
        asset: { code: "XLM", native: true },
        memo: "SDP-WC-test",
        memoType: "text",
      },
    ],
  },
  [STELLAR_METHOD_TRUST_ASSET]: {
    label: "stellar_trustAsset",
    params: [
      {
        code: "USDC",
        issuer: "GA5ZSEJYB37JRC5AVCIA5MOP4RHTM335X2KGX3IHOJAPP5RE34K4KZVN",
      },
      "1000",
    ],
  },
};

const METHOD_OPTIONS = Object.entries(METHOD_PRESETS).map(([value, { label }]) => ({
  value,
  label,
}));

const nowIso = () => new Date().toISOString();

export const WalletConnectDebugPage = () => {
  const [userId, setUserId] = useState(DEFAULT_USER_ID);
  const [status, setStatus] = useState<Status>("idle");
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [session, setSession] = useState<SessionTypes.Struct | null>(null);
  const [pairing, setPairing] = useState<PairingTypes.Struct | null>(null);
  const [proposals, setProposals] = useState<ProposalTypes.Struct[]>([]);
  const [metadata, setMetadata] = useState<SignClientTypes.Metadata | null>(
    null,
  );
  const [method, setMethod] = useState<string>(STELLAR_METHOD_SIGN_MESSAGE);
  const [paramsText, setParamsText] = useState<string>(
    JSON.stringify(METHOD_PRESETS[STELLAR_METHOD_SIGN_MESSAGE].params, null, 2),
  );
  const [chainId, setChainId] = useState<string>(STELLAR_TESTNET_CHAIN_ID);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const clientRef = useRef<WcSignClient | null>(null);
  const unsubscribersRef = useRef<Array<() => void>>([]);

  const pushLog = (label: string, payload: unknown) => {
    setLogs((prev) =>
      [{ at: nowIso(), label, payload }, ...prev].slice(0, LOG_LIMIT),
    );
  };

  const refreshSnapshot = () => {
    const client = clientRef.current;
    if (!client) return;
    setSession(client.getSession());
    setPairing(client.getPairing());
    setProposals(client.getProposals());
    setMetadata(client.getMetadata());
  };

  const teardownSubscriptions = () => {
    unsubscribersRef.current.forEach((fn) => fn());
    unsubscribersRef.current = [];
  };

  const bindSubscriptions = (client: WcSignClient) => {
    teardownSubscriptions();
    const events: SignClientTypes.Event[] = [
      "session_proposal",
      "session_update",
      "session_extend",
      "session_delete",
      "session_expire",
      "session_ping",
      "session_request",
      "session_event",
      "proposal_expire",
    ];
    events.forEach((event) => {
      const unsub = client.on(event, (args) => {
        pushLog(event, args);
        refreshSnapshot();
      });
      unsubscribersRef.current.push(unsub);
    });
  };

  const handleInit = async () => {
    setError(null);
    setStatus("initializing");
    try {
      const client = await WcSignClient.init(userId);
      clientRef.current = client;
      bindSubscriptions(client);
      refreshSnapshot();
      pushLog("init", { userId, metadata: client.getMetadata() });
      setIsInitialized(true);
      setStatus("ready");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      pushLog("init:error", message);
      setStatus("error");
    }
  };

  const handleConnect = async (newConnection: boolean) => {
    const client = clientRef.current;
    if (!client) return;
    setError(null);
    setUri(null);
    setStatus("connecting");
    try {
      const params: EngineTypes.ConnectParams = { ...DEFAULT_CONNECT_PARAMS };
      const nextUri = await client.connect(
        params,
        newConnection,
        (_p, s) => {
          pushLog("session_approved", s);
          refreshSnapshot();
          setStatus("connected");
          setUri(null);
        },
        (_p, e) => {
          pushLog("session_rejected", e.message);
          setError(e.message);
          setStatus("error");
        },
      );
      if (nextUri) {
        setUri(nextUri);
        pushLog("pairing_uri", nextUri);
      } else {
        pushLog("connect", "reused existing pairing, no URI");
      }
      refreshSnapshot();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      pushLog("connect:error", message);
      setStatus("error");
    }
  };

  const handleDisconnect = async () => {
    const client = clientRef.current;
    if (!client) return;
    try {
      await client.disconnect();
      pushLog("disconnected", { topic: session?.topic });
      refreshSnapshot();
      setStatus("ready");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      pushLog("disconnect:error", message);
    }
  };

  const handleMethodChange = (nextMethod: string) => {
    setMethod(nextMethod);
    const preset = METHOD_PRESETS[nextMethod];
    if (preset) {
      setParamsText(JSON.stringify(preset.params, null, 2));
    }
  };

  const handleSendRequest = async () => {
    const client = clientRef.current;
    if (!client) return;
    let parsedParams: unknown[];
    try {
      const parsed = JSON.parse(paramsText || "[]");
      if (!Array.isArray(parsed)) {
        throw new Error("params must be a JSON array (Stellar RPC expects [payload])");
      }
      parsedParams = parsed;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Invalid params JSON: ${message}`);
      pushLog("request:error", `Invalid params JSON: ${message}`);
      return;
    }

    try {
      await client.sendRequest(
        chainId,
        { method, params: parsedParams },
        (p, response) => pushLog("request:success", { p, response }),
        (p, e) => pushLog("request:error", { p, error: e.message }),
      );
      pushLog("request:sent", { chainId, method, params: parsedParams });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      pushLog("request:error", message);
    }
  };

  useEffect(() => {
    return () => {
      teardownSubscriptions();
    };
  }, []);

  const canConnect = status === "ready" || status === "connected";
  const canSendRequest = !!session && !!method;

  const summary = useMemo(
    () => ({
      status,
      uri,
      topic: session?.topic ?? pairing?.topic ?? null,
      pairingTopic: pairing?.topic ?? null,
      sessionExpiry: session?.expiry ?? null,
      peer: session?.peer ?? null,
      namespaces: session?.namespaces ?? null,
      proposalCount: proposals.length,
      metadata,
    }),
    [status, uri, session, pairing, proposals.length, metadata],
  );

  return (
    <div style={{ padding: "1.5rem", display: "grid", gap: "1rem" }}>
      <Heading size="md" as="h1">
        WalletConnect · Debug Session
      </Heading>
      <p>
        Simulation harness for the SDP × VIA Wallet WalletConnect pairing. This
        page is not part of the production dashboard flow — it exists to verify
        that <code>WcSignClient</code> initializes, produces a pairing URI, and
        surfaces the resulting session metadata (topic, pairing, session,
        proposal).
      </p>

      {error && (
        <Notification variant="error" title="WalletConnect error" isFilled>
          {error}
        </Notification>
      )}

      <section style={{ display: "grid", gap: "0.5rem" }}>
        <Heading size="sm" as="h2">
          1. Initialize
        </Heading>
        <Input
          fieldSize="sm"
          id="wc-user-id"
          label="User ID (storage prefix)"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        />
        <div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleInit}
            disabled={status === "initializing" || !userId}
          >
            {isInitialized ? "Re-initialize" : "Initialize SignClient"}
          </Button>
        </div>
      </section>

      <section style={{ display: "grid", gap: "0.5rem" }}>
        <Heading size="sm" as="h2">
          2. Pair
        </Heading>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleConnect(false)}
            disabled={!canConnect}
          >
            Connect (reuse pairing)
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleConnect(true)}
            disabled={!canConnect}
          >
            Connect (new pairing)
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDisconnect}
            disabled={!session}
          >
            Disconnect
          </Button>
        </div>
        {uri && (
          <div
            style={{
              display: "grid",
              gap: "0.5rem",
              justifyItems: "start",
              padding: "1rem",
              border: "1px solid var(--ion-color-step-100, #ddd)",
              borderRadius: 8,
            }}
          >
            <QRCodeSVG value={uri} size={220} />
            <textarea
              readOnly
              value={uri}
              style={{ width: "100%", minHeight: 60 }}
            />
          </div>
        )}
      </section>

      <section style={{ display: "grid", gap: "0.5rem" }}>
        <Heading size="sm" as="h2">
          3. Send a session request
        </Heading>
        <Select
          fieldSize="sm"
          id="wc-chain"
          label="Chain"
          value={chainId}
          onChange={(e) => setChainId(e.target.value)}
        >
          {CHAIN_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Select
          fieldSize="sm"
          id="wc-method"
          label="Method (VIA Wallet supported)"
          value={method}
          onChange={(e) => handleMethodChange(e.target.value)}
        >
          {METHOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Textarea
          fieldSize="sm"
          id="wc-params"
          label="params (JSON array — matches the wallet's RPC handler)"
          rows={8}
          value={paramsText}
          onChange={(e) => setParamsText(e.target.value)}
        />
        <div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSendRequest}
            disabled={!canSendRequest}
          >
            Send {method}
          </Button>
        </div>
      </section>

      <section style={{ display: "grid", gap: "0.5rem" }}>
        <Heading size="sm" as="h2">
          Session metadata
        </Heading>
        <pre
          style={{
            background: "var(--ion-color-step-50, #f6f6f6)",
            padding: "0.75rem",
            borderRadius: 8,
            overflow: "auto",
            maxHeight: 260,
          }}
        >
          {JSON.stringify(summary, null, 2)}
        </pre>
      </section>

      <section style={{ display: "grid", gap: "0.5rem" }}>
        <Heading size="sm" as="h2">
          Event log
        </Heading>
        <pre
          style={{
            background: "var(--ion-color-step-50, #f6f6f6)",
            padding: "0.75rem",
            borderRadius: 8,
            overflow: "auto",
            maxHeight: 320,
          }}
        >
          {logs.length === 0
            ? "No events yet."
            : logs
                .map(
                  (entry) =>
                    `[${entry.at}] ${entry.label}\n${JSON.stringify(
                      entry.payload,
                      null,
                      2,
                    )}`,
                )
                .join("\n\n")}
        </pre>
      </section>
    </div>
  );
};
